'use client';

import {
  assignOnboardingModelSplit,
  isOnboardingModelSplit,
  ONBOARDING_MODEL_SPLIT_FLAG,
  ONBOARDING_MODEL_SPLIT_PROP,
  type OnboardingModelSplit,
} from '@poppy/utils';
import flagService from '@/lib/posthog/flagService';
import { getPosthogClient } from '@/lib/posthog/posthog';

const STORAGE_KEY = 'poppy_onboarding_model_split';

/**
 * The side a person is on in the onboarding default-model split test, decided in the
 * browser the moment a tour lays out its chat node (see @poppy/utils onboardingModelSplit).
 * Null = the test is off (the flag is off, or PostHog has not answered yet), so the chat
 * keeps today's default model and no split property is written. The flag is read at that
 * moment only: a person assigned while it was on keeps their side in the node's data.
 */
export function resolveOnboardingModelSplit(
  userId: string | null | undefined,
  isTestOn: () => boolean = () => flagService.isFlagOn(ONBOARDING_MODEL_SPLIT_FLAG)
): OnboardingModelSplit | null {
  if (!userId || !isTestOn()) return null;
  return assignOnboardingModelSplit(userId);
}

/**
 * Remember the side for this person in the browser and on their PostHog person, so every
 * client event from now on carries `onboarding_model_split` (analyticsTrack.ts) and the
 * money read per side (cc Onboarding Tests) needs no join. Both writes fail soft.
 */
export function rememberOnboardingModelSplit(userId: string, split: OnboardingModelSplit): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, side: split.side }));
  } catch {
    // private window / blocked storage: the person property still carries it
  }
  try {
    getPosthogClient()?.capture('$set', { $set: { [ONBOARDING_MODEL_SPLIT_PROP]: split.side } });
  } catch {
    // PostHog blocked: the events still carry the side from storage
  }
}

/** The remembered side for this person, or null (another person, nothing remembered, no storage). */
export function rememberedOnboardingModelSplitSide(
  userId: string | null | undefined
): 'A' | 'B' | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { userId?: string; side?: string };
    return saved.userId === userId &&
      isOnboardingModelSplit({ id: 'onboarding_default_model', side: saved.side })
      ? (saved.side as 'A' | 'B')
      : null;
  } catch {
    return null;
  }
}
